import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

/**
 * Aviso legal y política de privacidad de la web pública.
 *
 * Dos cosas sobre cómo está escrito esto, porque importan:
 *
 * 1. **Lo que describe el programa es cierto y está comprobado.** Qué datos
 *    guarda, qué cookies pone, a quién salen y con qué papel se tratan: eso se
 *    ha mirado en el código, no se ha supuesto. Es lo que un texto legal suele
 *    tener mal, porque lo copia de otra web.
 *
 * 2. **Lo que decide una persona, no un programa, aparece marcado como
 *    pendiente y a la vista.** Los plazos de conservación, la base jurídica de
 *    cada tratamiento y los datos del titular no los puede inventar el código.
 *    Un documento legal con huecos rellenados a ojo es peor que no tenerlo:
 *    parece que cumples y no cumples.
 *
 * El aviso legal y la privacidad viven en el mismo fichero porque comparten los
 * datos del titular y la mitad de las advertencias.
 */

interface DatosLegales {
  titular: { nombre: string | null; nif: string | null; direccion: string | null; email: string | null };
  /** Los campos que fallan, cada uno con su motivo en lenguaje llano. */
  faltan: Record<string, string>;
  completo: boolean;
  cookies: string[];
  destinatarios: { alojamiento: boolean; correo: boolean; avisosMovil: boolean };
}

function Pendiente({ children }: { children: React.ReactNode }) {
  return <mark className="pendiente">Falta por decidir: {children}</mark>;
}

export default function Legal({ documento }: { documento: "aviso" | "privacidad" }) {
  const titulo = documento === "aviso" ? "Aviso legal" : "Política de privacidad";

  useEffect(() => {
    const anterior = document.title;
    document.title = `${titulo} · asocis`;
    return () => {
      document.title = anterior;
    };
  }, [titulo]);

  const datos = useQuery({ queryKey: ["legal"], queryFn: () => api<DatosLegales>("/legal") });
  const d = datos.data;

  return (
    <div className="publica">
      <header className="publica-barra">
        <Link to="/" className="publica-marca">
          <img src="/asocis.png" alt="asocis" className="publica-logotipo" />
        </Link>
        <nav className="fila">
          <Link to="/entrar" className="boton secundario">
            Entrar
          </Link>
        </nav>
      </header>

      <main className="documento-legal">
        <h1>{titulo}</h1>

        {d && !d.completo && (
          <div className="aviso atencion">
            <strong>Este documento está incompleto.</strong> Faltan datos del titular que la ley exige:
            <ul className="lista-legal" style={{ marginTop: 8, marginBottom: 8 }}>
              {Object.entries(d.faltan).map(([campo, motivo]) => (
                <li key={campo}>
                  {motivo} <code>PROVEEDOR_{campo.toUpperCase()}</code>
                </li>
              ))}
            </ul>
            Mientras tanto, esta página no sirve como aviso legal.
          </div>
        )}

        {documento === "aviso" ? <AvisoLegal datos={d} /> : <Privacidad datos={d} />}

        <p className="suave pequeno cierre-legal">
          Última revisión del texto: la de la versión del programa que estás usando. Los apartados marcados
          en amarillo los tiene que completar y revisar un asesor: describen decisiones tuyas, no lo que hace
          el programa.
        </p>
      </main>

      <footer className="publica-pie">
        <span>asocis · Gestión de asociaciones cannábicas</span>
        <span className="fila">
          <Link to="/aviso-legal">Aviso legal</Link>
          <Link to="/privacidad">Privacidad</Link>
        </span>
      </footer>
    </div>
  );
}

function Titular({ datos }: { datos?: DatosLegales }) {
  const t = datos?.titular;

  /*
   * Si un dato no pasa la comprobacion, se ensena el motivo en vez del dato.
   * Pintar "NIF CAMBIAR" como si fuera un NIF es lo que convierte un aviso
   * legal en un problema: parece que cumple.
   */
  const falta = (campo: string, valor: string | null | undefined, que: string) => {
    const motivo = datos?.faltan[campo];
    if (motivo) return <Pendiente>{motivo.replace(/^Falta /, "")}</Pendiente>;
    return valor ?? <Pendiente>{que} del titular</Pendiente>;
  };

  return (
    <ul className="lista-legal">
      <li>
        <strong>Titular:</strong> {falta("nombre", t?.nombre, "el nombre o la razón social")}
      </li>
      <li>
        <strong>NIF:</strong> {falta("nif", t?.nif, "el NIF")}
      </li>
      <li>
        <strong>Domicilio:</strong> {falta("direccion", t?.direccion, "el domicilio")}
      </li>
      <li>
        <strong>Contacto:</strong>{" "}
        {t?.email && !datos?.faltan.email ? (
          <a href={`mailto:${t.email}`}>{t.email}</a>
        ) : (
          <Pendiente>{datos?.faltan.email ?? "la dirección de contacto"}</Pendiente>
        )}
      </li>
      <li>
        <strong>Datos registrales:</strong>{" "}
        <Pendiente>si es una sociedad, el registro mercantil, el tomo y el folio; si eres autónomo, esta línea se quita</Pendiente>
      </li>
    </ul>
  );
}

function AvisoLegal({ datos }: { datos?: DatosLegales }) {
  return (
    <>
      <h2>Quién está detrás de esta web</h2>
      <Titular datos={datos} />

      <h2>Qué es asocis</h2>
      <p>
        asocis es un programa de gestión que se alquila a asociaciones cannábicas para llevar su registro de
        socios, sus existencias, su caja y su portal para socios. Esta web sirve para darlo a conocer y para
        que las asociaciones que lo tienen contratado accedan a él.
      </p>
      <p>
        El programa no vende ni distribuye ninguna sustancia, ni interviene en la actividad de las
        asociaciones que lo usan. Cada asociación es responsable de su propia actividad y de cumplir la
        normativa que le aplique.
      </p>

      <h2>Condiciones de uso</h2>
      <p>
        El acceso a la parte privada requiere una cuenta facilitada por la asociación correspondiente o por
        el titular de esta web. Quien tenga una cuenta es responsable de mantener su contraseña en secreto y
        de lo que se haga con ella.
      </p>
      <p>
        No se permite intentar acceder a datos de otras asociaciones, alterar el funcionamiento del servicio
        ni utilizarlo para fines distintos de los previstos.
      </p>

      <h2>Condiciones del servicio y del pago</h2>
      <p>
        <Pendiente>
          las condiciones de contratación: precio, periodo de prueba, forma y plazo de pago, qué pasa si no
          se paga, con cuánta antelación puede darse de baja una asociación, y qué garantía de servicio se
          ofrece
        </Pendiente>
      </p>
      <p className="suave">
        Lo que el programa hace hoy, por si sirve de punto de partida: avisa a la asociación una semana antes
        del vencimiento, deja siete días de margen y después pasa a solo lectura, sin borrar nada y dejando
        siempre disponible la descarga de todos sus datos.
      </p>

      <h2>Propiedad intelectual</h2>
      <p>
        El programa, su código y su diseño pertenecen al titular. Los datos que cada asociación introduce
        siguen siendo suyos, y puede descargarlos completos en cualquier momento desde sus ajustes, incluido
        el día que decida dejar de usarlo.
      </p>

      <h2>Legislación aplicable</h2>
      <p>
        <Pendiente>la ley aplicable y los juzgados competentes en caso de conflicto</Pendiente>
      </p>
    </>
  );
}

function Privacidad({ datos }: { datos?: DatosLegales }) {
  return (
    <>
      <h2>Quién trata tus datos</h2>
      <Titular datos={datos} />

      <h2>Dos situaciones distintas, y conviene no mezclarlas</h2>
      <p>
        Esta web y este programa tratan datos de dos grupos de personas, y el papel del titular no es el
        mismo en cada uno:
      </p>
      <ul className="lista-legal">
        <li>
          <strong>Las personas de las asociaciones que contratan el programa</strong> —quien pide
          información, quien da de alta el club, quien tiene una cuenta del equipo—. Aquí el titular decide
          qué hace con esos datos: es el <em>responsable</em>.
        </li>
        <li>
          <strong>Los socios de cada asociación</strong> —sus nombres, documentos, fotos, consumo—. Esos
          datos los introduce la asociación, son suyos y ella decide qué hacer con ellos. El titular solo los
          guarda y los procesa por cuenta de la asociación: es el <em>encargado del tratamiento</em>. Si eres
          socio de un club y quieres ejercer tus derechos, tienes que dirigirte a tu asociación, no aquí.
        </li>
      </ul>
      <p>
        <Pendiente>
          el contrato de encargado del tratamiento que hay que firmar con cada asociación. Lo exige el
          artículo 28 del RGPD y no lo puede generar un programa
        </Pendiente>
      </p>

      <h2>Qué datos se tratan y para qué</h2>
      <ul className="lista-legal">
        <li>
          <strong>De quien contacta o da de alta un club:</strong> nombre, correo electrónico y nombre de la
          asociación, para crear la cuenta, prestar el servicio y facturarlo.
        </li>
        <li>
          <strong>De las cuentas del equipo de cada club:</strong> nombre, correo y rol, para dar acceso y
          para dejar constancia de quién hizo cada operación.
        </li>
        <li>
          <strong>De los socios de cada asociación:</strong> los que la asociación decida introducir
          —identificación, contacto, foto, consumo, pagos—, tratados únicamente por cuenta de ella.
        </li>
      </ul>
      <p>
        <Pendiente>la base jurídica de cada tratamiento: contrato, interés legítimo, consentimiento u obligación legal</Pendiente>
      </p>

      <h2>Cuánto tiempo se guardan</h2>
      <p>
        <Pendiente>
          los plazos de conservación. Ten en cuenta que hay obligaciones que tiran en sentido contrario: la
          contabilidad hay que conservarla años, y los datos personales que ya no hacen falta hay que
          borrarlos
        </Pendiente>
      </p>
      <p className="suave">
        Lo que el programa hace hoy: guarda una copia de seguridad de cada asociación cada noche y conserva
        las de los últimos catorce días. Cuando se borran los datos de un socio a petición suya, desaparecen
        de la base al momento; las copias anteriores los siguen teniendo hasta que caducan.
      </p>

      <h2>Quién más ve estos datos</h2>
      <p>Los datos no se venden ni se ceden a nadie. Los únicos que intervienen técnicamente son:</p>
      <ul className="lista-legal">
        <li>
          <strong>El proveedor de alojamiento</strong>, donde vive el servidor y la base de datos.{" "}
          <Pendiente>su nombre y el país donde están los servidores</Pendiente>
        </li>
        {datos?.destinatarios.correo && (
          <li>
            <strong>El proveedor de correo</strong>, que entrega los avisos y los enlaces de recuperación de
            contraseña. <Pendiente>su nombre</Pendiente>
          </li>
        )}
        <li>
          <strong>Los servicios de avisos de Google y Apple</strong>, solo si un socio activa las
          notificaciones en su móvil desde el portal. Reciben una dirección técnica de su dispositivo, no sus
          datos personales.
        </li>
      </ul>

      <h2>Cookies</h2>
      <p>
        Esta web <strong>no usa analítica, ni rastreadores, ni publicidad, ni fuentes o scripts de terceros</strong>.
        Las únicas cookies que se instalan son las estrictamente necesarias para mantener la sesión de quien
        ha entrado con su cuenta{datos?.cookies.length ? ` (${datos.cookies.join(", ")})` : ""}, y se borran
        al cerrar sesión.
      </p>
      <p>
        Por eso no hay ventana de cookies: las técnicas no la necesitan. Si algún día se añade analítica,
        habrá que poner el aviso y pedir consentimiento antes de instalarla.
      </p>

      <h2>Tus derechos</h2>
      <p>
        Puedes pedir acceso a tus datos, su rectificación o su supresión, así como limitar u oponerte a su
        tratamiento y solicitar su portabilidad. Escribe a la dirección de contacto de arriba.
      </p>
      <p>
        Si crees que tus datos no se están tratando como deben, puedes reclamar ante la{" "}
        <a href="https://www.aepd.es" target="_blank" rel="noreferrer">
          Agencia Española de Protección de Datos
        </a>
        .
      </p>

      <h2>Seguridad</h2>
      <p>
        Los datos de cada asociación están separados de los de las demás. Las contraseñas se guardan
        cifradas y nadie —tampoco el titular— puede verlas. Toda la actividad queda registrada con quién y
        cuándo, la conexión va cifrada, y hay copia de seguridad diaria.
      </p>
    </>
  );
}
